import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Save, ScanLine, LayoutTemplate, Bookmark } from "lucide-react";
import { toast } from "sonner";
import { guToLatin } from "@/i18n";
import BarcodeScanner from "@/components/BarcodeScanner";
import VoiceRecorder from "@/components/VoiceRecorder";
import SearchableSelect from "@/components/SearchableSelect";
import { convertRate, areUnitsConvertible } from "@/lib/units";

const UNITS = ["kg", "gm", "L", "ml", "pcs", "pack", "dozen", "bag", "box", "bundle"];

export default function NewBill({ prefilledLines = null, prefilledCustomer = "" }) {
  const { t, user, lang } = useApp();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const templateId = sp.get("template");
  const isOwner = user?.role === "owner";
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [customerName, setCustomerName] = useState(prefilledCustomer);
  const [customerId, setCustomerId] = useState(null);
  const [lines, setLines] = useState(prefilledLines || [{ name_en: "", name_gu: "", unit: "pcs", quantity: 1, rate: 0, total: 0, item_id: null }]);
  const [discount, setDiscount] = useState(0);
  const [paid, setPaid] = useState(0);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [tplPickerOpen, setTplPickerOpen] = useState(false);
  const [tplSaveOpen, setTplSaveOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  // Quick-create item modal (from item searchable dropdown "+ Create New")
  const [qcOpen, setQcOpen] = useState(false);
  const [qcLineIdx, setQcLineIdx] = useState(null);
  const [qcForm, setQcForm] = useState({ name_en: "", name_gu: "", unit: "pcs", cost_price: 0, selling_price: 0, stock: 0 });
  // Quick-create customer
  const [ccOpen, setCcOpen] = useState(false);
  const [ccName, setCcName] = useState("");

  useEffect(() => {
    api.get("/items").then((r) => setItems(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/templates").then((r) => setTemplates(r.data));
  }, []);

  // Load template from URL param
  useEffect(() => {
    if (!templateId) return;
    api.get(`/templates/${templateId}`).then((r) => {
      applyTemplate(r.data);
    }).catch(() => {});
    // eslint-disable-next-line
  }, [templateId, items]);

  const applyTemplate = (tp) => {
    if (!tp) return;
    if (tp.customer_name) setCustomerName(tp.customer_name);
    if (tp.customer_id) setCustomerId(tp.customer_id);
    if (tp.notes) setNotes(tp.notes);
    setLines(tp.lines.map((l) => {
      const it = l.item_id ? items.find((x) => x.id === l.item_id) : null;
      const rate = l.rate > 0 ? l.rate : (it ? it.selling_price : 0);
      return { ...l, rate, total: (l.quantity || 0) * rate };
    }));
    toast.success(`Template: ${tp.name}`);
    setTplPickerOpen(false);
  };

  const setLine = (i, patch) => {
    setLines((prev) => prev.map((l, idx) => {
      if (idx !== i) return l;
      let next = { ...l, ...patch };
      // Req #7: unit change auto-recomputes rate if units are convertible.
      // Skip when patch also sets rate (e.g. catalog pick fills its own selling_price).
      if (patch.unit !== undefined && patch.rate === undefined && patch.unit !== l.unit && l.rate > 0 && areUnitsConvertible(l.unit, patch.unit)) {
        const conv = convertRate(l.rate, l.unit, patch.unit);
        if (conv !== null && conv !== undefined) next.rate = Number(conv.toFixed(4));
      }
      next.total = (next.quantity || 0) * (next.rate || 0);
      return next;
    }));
  };

  // Req #8: manual line-total change → adjust quantity keeping rate constant
  const setLineTotal = (i, newTotal) => {
    setLines((prev) => prev.map((l, idx) => {
      if (idx !== i) return l;
      const rate = Number(l.rate) || 0;
      if (rate <= 0) return { ...l, total: newTotal };
      const qty = Number((newTotal / rate).toFixed(4));
      return { ...l, quantity: qty, total: qty * rate };
    }));
  };

  const pickCatalog = (i, itemId) => {
    const it = items.find((x) => x.id === itemId);
    if (!it) return;
    setLine(i, { item_id: it.id, name_en: it.name_en, name_gu: it.name_gu, unit: it.unit, rate: it.selling_price });
  };

  const addLine = () => setLines([...lines, { name_en: "", name_gu: "", unit: "pcs", quantity: 1, rate: 0, total: 0, item_id: null }]);
  const delLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const addByBarcode = async (code) => {
    try {
      const r = await api.get(`/items/by-barcode/${encodeURIComponent(code)}`);
      const it = r.data;
      // If a line already has this item, increment; else add new line
      const existingIdx = lines.findIndex((l) => l.item_id === it.id);
      if (existingIdx >= 0) {
        setLine(existingIdx, { quantity: (lines[existingIdx].quantity || 0) + 1 });
      } else {
        // Replace last empty line, else append
        const emptyIdx = lines.findIndex((l) => !l.name_en && !l.item_id);
        const newLine = { item_id: it.id, name_en: it.name_en, name_gu: it.name_gu, unit: it.unit, quantity: 1, rate: it.selling_price, total: it.selling_price };
        if (emptyIdx >= 0) setLines(lines.map((l, idx) => idx === emptyIdx ? newLine : l));
        else setLines([...lines, newLine]);
      }
      toast.success(`${it.name_en} added`);
    } catch (err) {
      toast.error(t("barcode_not_found"));
    }
  };

  const saveAsTemplate = async () => {
    if (!tplName.trim()) return toast.error("Name required");
    const validLines = lines.filter((l) => l.name_en && l.quantity > 0);
    if (validLines.length === 0) return toast.error("Add at least one line");
    try {
      await api.post("/templates", {
        name: tplName.trim(),
        customer_id: customerId,
        customer_name: customerName,
        notes,
        lines: validLines.map((l) => ({
          item_id: l.item_id, name_en: l.name_en, name_gu: l.name_gu,
          unit: l.unit, quantity: l.quantity, rate: l.rate,
        })),
      });
      toast.success(t("template_saved"));
      setTplSaveOpen(false); setTplName("");
      api.get("/templates").then((r) => setTemplates(r.data));
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const appendVoiceLines = (voiceLines) => {
    // For each voice line, append a bill line. If matched to catalog, use its rate.
    const newLines = voiceLines.map((vl) => {
      const it = vl.matched_item_id ? items.find((x) => x.id === vl.matched_item_id) : null;
      const rate = it ? it.selling_price : 0;
      const qty = Number(vl.quantity) || 1;
      return {
        item_id: it?.id || null,
        name_en: vl.name_en || (it?.name_en || ""),
        name_gu: vl.name_gu || (it?.name_gu || ""),
        unit: vl.unit || (it?.unit || "pcs"),
        quantity: qty,
        rate,
        total: qty * rate,
      };
    });
    // If the current lines are all empty, replace them; otherwise append.
    setLines((prev) => {
      const hasContent = prev.some((l) => l.name_en || l.item_id);
      return hasContent ? [...prev, ...newLines] : newLines;
    });
    toast.success(`+${newLines.length} lines`);
  };

  const subtotal = useMemo(() => lines.reduce((s, l) => s + (l.total || 0), 0), [lines]);
  const total = Math.max(0, subtotal - (discount || 0));

  const save = async () => {
    for (const l of lines) {
      if (!l.name_en.trim() || !l.quantity || l.quantity <= 0) {
        return toast.error(t("incomplete_row"));
      }
    }
    setBusy(true);
    try {
      const status = paid >= total ? "paid" : paid > 0 ? "partial" : "credit";
      const r = await api.post("/bills", {
        customer_id: customerId, customer_name: customerName,
        lines: lines.map((l) => ({ ...l, total: l.quantity * l.rate })),
        discount, amount_paid: paid, status, notes, language: lang,
      });
      toast.success(t("bill_created"));
      nav(`/bills/${r.data.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 pb-24">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="font-heading text-3xl font-bold text-stone-900">{t("new_bill")}</h1>
        <div className="flex gap-2 flex-wrap">
          <VoiceRecorder onLines={appendVoiceLines} />
          <Button variant="outline" onClick={() => setScanOpen(true)} data-testid="bill-scan-btn"
                  className="h-11 rounded-xl border-stone-300 font-medium">
            <ScanLine className="w-4 h-4 mr-2" /> {t("scan_barcode")}
          </Button>
          <Button variant="outline" onClick={() => setTplPickerOpen(true)} data-testid="bill-use-template-btn"
                  className="h-11 rounded-xl border-stone-300 font-medium">
            <LayoutTemplate className="w-4 h-4 mr-2" /> {t("use_template")}
          </Button>
          <Button variant="outline" onClick={() => setTplSaveOpen(true)} data-testid="bill-save-template-btn"
                  className="h-11 rounded-xl border-stone-300 font-medium">
            <Bookmark className="w-4 h-4 mr-2" /> {t("save_as_template")}
          </Button>
        </div>
      </div>

      <Card className="p-4 rounded-2xl space-y-3">
        <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("customer")}</div>
        <SearchableSelect
          value={customerId}
          onChange={(id, c) => { setCustomerId(id); setCustomerName(c ? c.label : ""); }}
          options={customers.map((c) => ({ id: c.id, label: c.name, sublabel: c.phone }))}
          placeholder={t("customer")}
          allowCreate
          onCreate={(q) => { setCcName(q); setCcOpen(true); }}
          createLabel={t("create_new_customer")}
          testid="bill-customer-select"
        />
      </Card>

      <div className="space-y-3">
        {lines.map((l, i) => (
          <Card key={i} className="p-4 rounded-2xl border-stone-200" data-testid={`bill-line-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-widest text-stone-500">#{i + 1}</div>
              <button onClick={() => delLine(i)} data-testid={`bill-line-del-${i}`} className="h-8 w-8 rounded-lg bg-red-50 grid place-items-center text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
            <SearchableSelect
              value={l.item_id}
              onChange={(id) => id ? pickCatalog(i, id) : setLine(i, { item_id: null })}
              options={items.map((it) => ({ id: it.id, label: it.name_en, sublabel: it.name_gu }))}
              placeholder={`— ${t("pick_from_catalog")} —`}
              allowCreate
              onCreate={(q) => { setQcLineIdx(i); setQcForm({ name_en: q, name_gu: "", unit: "pcs", cost_price: 0, selling_price: 0, stock: 0 }); setQcOpen(true); }}
              createLabel={t("create_new_item")}
              testid={`bill-line-pick-${i}`}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
              <Input placeholder={t("name_en")} value={l.name_en} onChange={(e) => setLine(i, { name_en: e.target.value })} data-testid={`bill-line-en-${i}`} className="h-12 rounded-lg" />
              <Input placeholder={t("name_gu")} value={l.name_gu} onChange={(e) => setLine(i, { name_gu: e.target.value })} data-testid={`bill-line-gu-${i}`} className="h-12 rounded-lg font-gujarati" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1 sm:hidden">{t("quantity")}</label>
                <Input type="text" placeholder={t("quantity")} value={l.quantity} onChange={(e) => setLine(i, { quantity: parseFloat(guToLatin(e.target.value)) || 0 })} data-testid={`bill-line-qty-${i}`} className="h-12 rounded-lg font-mono w-full" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1 sm:hidden">{t("unit")}</label>
                <select value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} data-testid={`bill-line-unit-${i}`} className="h-12 w-full rounded-lg border border-stone-300 px-2">
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1 sm:hidden">{t("rate")}</label>
                <Input type="number" placeholder={t("rate")} value={l.rate} onChange={(e) => setLine(i, { rate: parseFloat(e.target.value) || 0 })} disabled={!isOwner && l.item_id} data-testid={`bill-line-rate-${i}`} className="h-12 rounded-lg font-mono w-full" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1 sm:hidden">{t("amount")}</label>
                <Input type="number" placeholder={t("amount")} value={Number((l.quantity * l.rate).toFixed(2))} onChange={(e) => setLineTotal(i, parseFloat(e.target.value) || 0)} data-testid={`bill-line-amount-${i}`} className="h-12 rounded-lg font-mono bg-stone-50 w-full" />
              </div>
            </div>
          </Card>
        ))}
        <Button onClick={addLine} variant="outline" data-testid="bill-add-line" className="w-full h-12 rounded-xl">
          <Plus className="w-4 h-4 mr-2" /> {t("add_row")}
        </Button>
      </div>

      <Card className="p-4 rounded-2xl space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1">{t("discount")}</div>
            <Input type="number" value={discount} onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)} data-testid="bill-discount" className="h-12 rounded-lg" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-1">{t("paid")}</div>
            <Input type="number" value={paid} onChange={(e) => setPaid(parseFloat(e.target.value) || 0)} data-testid="bill-paid" className="h-12 rounded-lg" />
          </div>
        </div>
        <Input placeholder={t("notes")} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="bill-notes" className="h-12 rounded-lg" />
      </Card>

      <div className="fixed bottom-16 md:bottom-0 left-0 right-0 md:left-auto md:right-6 md:bottom-6 md:w-96 bg-white border-t md:border md:rounded-2xl border-stone-200 p-3 sm:p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-30">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="min-w-0">
            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-stone-500">{t("total")}</div>
            <div className="font-mono text-2xl sm:text-3xl font-bold text-stone-900 truncate" data-testid="bill-total">₹{total.toFixed(2)}</div>
          </div>
          <Button onClick={save} disabled={busy || lines.length === 0} data-testid="bill-save"
                  className="h-12 sm:h-14 px-3 sm:px-6 rounded-xl bg-[#166534] hover:bg-[#14532D] text-white font-semibold text-sm sm:text-base whitespace-nowrap">
            <Save className="w-4 h-4 mr-1 sm:mr-2" /> {t("create_bill")}
          </Button>
        </div>
      </div>

      <BarcodeScanner open={scanOpen} onOpenChange={setScanOpen} onDetected={addByBarcode} />

      <Dialog open={tplPickerOpen} onOpenChange={setTplPickerOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("pick_template")}</DialogTitle></DialogHeader>
          {templates.length === 0 && <div className="text-sm text-stone-500 py-6 text-center" data-testid="picker-empty">{t("no_templates")}</div>}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {templates.map((tp) => (
              <button key={tp.id} onClick={() => applyTemplate(tp)} data-testid={`picker-tpl-${tp.id}`}
                      className="w-full text-left p-3 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors">
                <div className="font-heading font-semibold">{tp.name}</div>
                <div className="text-xs text-stone-500">{tp.lines.length} lines · {tp.customer_name || "—"}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={tplSaveOpen} onOpenChange={setTplSaveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("save_as_template")}</DialogTitle></DialogHeader>
          <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder={t("template_name")} data-testid="tpl-save-name" className="h-12 rounded-lg" />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setTplSaveOpen(false)}>{t("cancel")}</Button>
            <Button onClick={saveAsTemplate} data-testid="tpl-save-submit" className="bg-[#B45309] hover:bg-[#92400E] text-white">{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick-create item (from item picker "+ Create new item") */}
      <Dialog open={qcOpen} onOpenChange={setQcOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("create_new_item")}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input placeholder={t("name_en")} value={qcForm.name_en} onChange={(e) => setQcForm({ ...qcForm, name_en: e.target.value })} data-testid="qc-name-en" className="h-12 rounded-lg" />
            <Input placeholder={t("name_gu")} value={qcForm.name_gu} onChange={(e) => setQcForm({ ...qcForm, name_gu: e.target.value })} data-testid="qc-name-gu" className="h-12 rounded-lg font-gujarati" />
            <div className="grid grid-cols-2 gap-2">
              <select value={qcForm.unit} onChange={(e) => setQcForm({ ...qcForm, unit: e.target.value })} data-testid="qc-unit" className="h-12 rounded-lg border border-stone-300 px-3">
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
              <Input type="number" placeholder={t("selling_price")} value={qcForm.selling_price} onChange={(e) => setQcForm({ ...qcForm, selling_price: parseFloat(e.target.value) || 0 })} data-testid="qc-price" className="h-12 rounded-lg" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setQcOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="qc-save" className="bg-[#B45309] hover:bg-[#92400E] text-white"
                    onClick={async () => {
                      if (!qcForm.name_en.trim()) return toast.error("Name required");
                      try {
                        const r = await api.post("/items", qcForm);
                        setItems((prev) => [...prev, r.data]);
                        if (qcLineIdx !== null) {
                          setLine(qcLineIdx, {
                            item_id: r.data.id, name_en: r.data.name_en, name_gu: r.data.name_gu,
                            unit: r.data.unit, rate: r.data.selling_price,
                          });
                        }
                        toast.success(t("item_saved"));
                        setQcOpen(false);
                      } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                    }}>{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick-create customer */}
      <Dialog open={ccOpen} onOpenChange={setCcOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("create_new_customer")}</DialogTitle></DialogHeader>
          <Input value={ccName} onChange={(e) => setCcName(e.target.value)} placeholder={t("name")} data-testid="cc-name" className="h-12 rounded-lg" />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setCcOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="cc-save" className="bg-[#B45309] hover:bg-[#92400E] text-white"
                    onClick={async () => {
                      if (!ccName.trim()) return toast.error("Name required");
                      try {
                        const r = await api.post("/customers", { name: ccName.trim(), phone: "", address: "", language: lang, is_bulk: false });
                        setCustomers((prev) => [...prev, r.data]);
                        setCustomerId(r.data.id); setCustomerName(r.data.name);
                        toast.success("Saved"); setCcOpen(false); setCcName("");
                      } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                    }}>{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
